import React, { useRef } from 'react';
import { Button, Space, Typography, theme } from 'antd';
import { PlusOutlined, DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import HallazgoFields from 'components/hallazgos/HallazgoFields';
import HallazgoFotosUpload from 'components/hallazgos/HallazgoFotosUpload';

const { useToken } = theme;

// Mapa camelCase (HallazgoFields) → snake_case (estado del voucher)
const FIELD_MAP = {
  idComponente: 'id_component',
  idAscensor: 'id_ascensor',
  mostrarEnComprobante: 'mostrar_en_comprobante',
};

const DEFAULT_HALLAZGO = {
  tipo: 'INCIDENCIA',
  descripcion: '',
  severidad: 'MEDIA',
  id_ascensor: null,
  id_component: null,
  mostrar_en_comprobante: true,
};

/**
 * Sección controlada de captura de hallazgos para emergencias.
 *
 * Props:
 *   value: array de hallazgos { tipo, descripcion, severidad, id_ascensor, id_component, _key }
 *   onChange: setter del array
 *   equiposSeleccionados: [{ nombre, id }] equipos del form ya resueltos a id
 *   fotosRefsMap: React.MutableRefObject<Map<number, React.Ref>>  (opcional)
 *     Map keyed by _key → ref de HallazgoFotosUpload (modo create).
 *     El padre lo usa post-submit para llamar uploadPending(idHallazgo) por índice.
 *     Si no se pasa, el componente funciona igual sin fotos.
 */
function HallazgosVoucherSection({
  value = [],
  onChange,
  equiposSeleccionados = [],
  fotosRefsMap,
}) {
  const { token } = useToken();
  const keyCounterRef = useRef(0);

  const addHallazgo = () => {
    keyCounterRef.current += 1;
    const nuevo = { ...DEFAULT_HALLAZGO, _key: keyCounterRef.current };
    // Auto-seleccionar si solo hay un equipo disponible
    if (equiposSeleccionados.length === 1) {
      nuevo.id_ascensor = equiposSeleccionados[0].id;
    }
    onChange([...value, nuevo]);
  };

  const removeHallazgo = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  // Opciones del selector de ascensor para HallazgoFields
  const ascensorOptions = equiposSeleccionados.map((eq) => ({
    value: eq.id,
    label: eq.nombre,
  }));

  return (
    <div
      style={{
        border: `1px solid ${token.colorWarningBorder}`,
        borderRadius: token.borderRadiusLG,
        padding: token.paddingMD,
        background: token.colorWarningBg,
      }}
    >
      {/* Header de la sección */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: value.length > 0 ? token.marginMD : 0,
        }}
      >
        <Space>
          <WarningOutlined style={{ color: token.colorWarning, fontSize: 16 }} />
          <Typography.Text strong style={{ color: token.colorWarningText, fontSize: 14 }}>
            Hallazgos detectados en emergencia
          </Typography.Text>
          {value.length > 0 && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              ({value.length} registrado{value.length !== 1 ? 's' : ''})
            </Typography.Text>
          )}
        </Space>
        <Button type="dashed" icon={<PlusOutlined />} onClick={addHallazgo} size="small">
          Agregar hallazgo
        </Button>
      </div>

      {/* Lista de hallazgos */}
      {value.map((hallazgo, idx) => (
        <div
          key={hallazgo._key ?? idx}
          style={{
            background: token.colorBgContainer,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: token.paddingSM,
            marginBottom: idx < value.length - 1 ? token.marginSM : 0,
          }}
        >
          {/* Botón eliminar alineado a la derecha */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removeHallazgo(idx)}
              size="small"
            />
          </div>

          {/* Átomo unificado de captura */}
          <HallazgoFields
            value={{
              tipo: hallazgo.tipo,
              descripcion: hallazgo.descripcion,
              severidad: hallazgo.severidad,
              idComponente: hallazgo.id_component ?? null,
              idAscensor: hallazgo.id_ascensor ?? null,
              mostrarEnComprobante: hallazgo.mostrar_en_comprobante,
            }}
            onChange={(patch) => {
              const next = { ...hallazgo };
              for (const [k, v] of Object.entries(patch)) {
                next[FIELD_MAP[k] || k] = v;
              }
              onChange(value.map((h, i) => (i === idx ? next : h)));
            }}
            equipoId={hallazgo.id_ascensor ?? null}
            ascensorOptions={ascensorOptions}
            show={{
              tipo: true,
              descripcion: true,
              severidad: true,
              componente: true,
              ascensor: true,
              mostrarEnComprobante: true,
            }}
            fotos={
              fotosRefsMap && (
                <HallazgoFotosUpload
                  ref={(el) => {
                    const key = hallazgo._key ?? idx;
                    if (el) fotosRefsMap.current.set(key, el);
                    else fotosRefsMap.current.delete(key);
                  }}
                  idHallazgo={null}
                />
              )
            }
          />
        </div>
      ))}

      {/* Mensaje vacío */}
      {value.length === 0 && (
        <Typography.Text
          type="secondary"
          style={{ fontSize: 12, display: 'block', marginTop: token.marginXS }}
        >
          Sin hallazgos registrados. Hacé clic en &ldquo;Agregar hallazgo&rdquo; para capturar
          incidencias o sugerencias detectadas.
        </Typography.Text>
      )}
    </div>
  );
}

export default HallazgosVoucherSection;
